import { AppstoreFilled, CaretDownFilled, DeleteOutlined, FilterOutlined, PlusOutlined, SortAscendingOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { Button, Dropdown, Input, Layout, MenuProps, Popover, Radio, RadioChangeEvent, Space, Spin, Upload } from 'antd';
import debounce from 'lodash.debounce';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { getDefaultPaginationSettings, getEnumNameFromValue, handleResponse, hasFlag, removeCommasAndPercentSign } from '../../../Helper';
import { graphicApi } from '../../../api';
import { SubPermissions, User, getPermissionErrorMessage } from '../../../model/AccountModel';
import { BadgeListInfo, BadgeListInfoPaginated, BadgeStudioFilterBy, BadgeStudioSortOptions, BadgeStudioStatus } from '../../../model/BadgeModel';
import { OperatorsDetail, PaginationSetting, ResponseObjectEntity, SortDirections, ViewType } from '../../../model/CommonModel';
import { BadgeDirectoryInfo, GraphicActionType, GraphicDirectoryInfo } from '../../../model/GraphicModel';
import { Logger } from '../../../model/LoggingModel';
import { BadgeStudioIcon } from '../../../resources/Icons';
import { useStoreDispatch, useStoreSelector } from '../../../store';
import { setActionType } from '../../../store/badge/actions';
import { actionTypeSelector } from '../../../store/badge/selectors';
import { ArchiveModal, CardView, FilterByContent, ListView, SortByContent } from '../../badge';
import { WithNewGraphicModal } from '../../common';
import { BadgeDirectoryModal } from '../BadgeDirectoryModal/BadgeDirectoryModal';
import styles from './badgeDirectory.module.scss';

const { Header, Content } = Layout;
const { Search } = Input;
const { Group: RadioGroup, Button: RadioButton } = Radio;

const defaultBadgeStudioFilterBy = { DoubleSided: false, Landscape: false, OperatorName: [], Portrait: false, Status: BadgeStudioStatus.New };
const user: User = getUser();
const canEditBadge: boolean = User.getSubComponentPermission(user, SubPermissions.Badges_Badges_Edit).allowed;

type Props = {
	isModal: boolean;
	isMaps: boolean;
};

const BadgeDirectory: FC<Props> = WithNewGraphicModal(({ uploadProps, handleOnOpen, handleOnOpenNewModal }) => {
	const dispatch = useStoreDispatch();
	const graphicActionType: GraphicActionType = useStoreSelector<GraphicActionType>(actionTypeSelector);
	const [open, setOpen] = useState(false);
	const [sortOpen, setSortOpen] = useState(false);
	const [currentView, setCurrentView] = useState<ViewType>(ViewType.Card);
	const [filterByData, setFilterByData] = useState<BadgeStudioFilterBy>(defaultBadgeStudioFilterBy);
	const [sortField, setSortField] = useState<BadgeStudioSortOptions>(BadgeStudioSortOptions.Name);
	const [sortDirection, setSortDirection] = useState<SortDirections>(SortDirections.Ascend);
	const [activeFilter, setActiveFilter] = useState<boolean>(false);
	const [activeSort, setActiveSort] = useState<boolean>(false);
	const [badgeOperators, setBadgeOperators] = useState<OperatorsDetail[]>([]);
	const [pagination, setPagination] = useState<PaginationSetting>({
		...getDefaultPaginationSettings(),
		SortField: 'Name',
		SortDirection: SortDirections.Ascend,
		PageSize: 12,
	});
	const [searchText, setSearchText] = useState<string>('');
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [disableActionButton, setDisableActionButton] = useState<boolean>(false);
	const [badgeListInfo, setBadgeListInfo] = useState<BadgeListInfo[]>([]);
	const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);

	const hide = () => {
		setOpen(false);
	};

	const hideSort = () => {
		setSortOpen(false);
	};

	const handleOpenChange = (newOpen: boolean) => {
		setOpen(newOpen);
	};

	const handleSortOpenChange = (newOpen: boolean) => {
		setSortOpen(newOpen);
	};

	useEffect(() => {
		getBadgeList(pagination, filterByData);

		graphicApi.getOperatorsDetails().then((response: ResponseObjectEntity<OperatorsDetail[]>) => {
			if (!handleResponse(response)) {
				setBadgeOperators(response.Entity);
			}
		});
	}, []);

	const getBadgeList = (pagination: PaginationSetting, filterByData?: BadgeStudioFilterBy) => {
		setIsLoading(true);
		graphicApi
			.getBadgeList(pagination, filterByData)
			.then((response: ResponseObjectEntity<BadgeListInfoPaginated>) => {
				if (!handleResponse(response)) {
					setPagination({ ...pagination, TotalItems: response.Entity.TotalItemsPaginated });
					setBadgeListInfo(response.Entity.BadgesListInfo);
				}
			})
			.catch(e => Logger.writeErrorLog(`${e.name}: ${e.message}`))
			.finally(() => setIsLoading(false));
	};

	const onClearFilterBy = () => {
		setFilterByData(defaultBadgeStudioFilterBy);
		if (activeFilter) {
			hide();
			setActiveFilter(false);
		}
		getBadgeList(pagination, defaultBadgeStudioFilterBy);
	};

	const onClearSortBy = () => {
		setSortField(BadgeStudioSortOptions.Name);
		setSortDirection(SortDirections.Ascend);
		const newPagination = {
			...pagination,
			SortField: getEnumNameFromValue(BadgeStudioSortOptions, sortField),
			SortDirection: SortDirections.Ascend,
		};
		if (activeSort) {
			hideSort();
			setActiveSort(false);
		}
		getBadgeList(newPagination, filterByData);
	};

	const onFilterBy = () => {
		hide();
		setActiveFilter(true);
		getBadgeList(pagination, filterByData);
	};

	const onSortBy = () => {
		hideSort();
		setActiveSort(true);
		const newPagination: PaginationSetting = {
			...pagination,
			SortField: getEnumNameFromValue(BadgeStudioSortOptions, sortField),
			SortDirection: sortDirection,
		};
		getBadgeList(newPagination, filterByData);
	};

	const callSearch = useCallback(
		debounce(async (value: string) => {
			const tempPagination: PaginationSetting = { ...pagination, TotalItems: 0, PageNumber: 1, SearchedValue: value };
			setPagination(tempPagination);
			getBadgeList(tempPagination, filterByData);
		}, 500),
		[pagination?.SearchedValue]
	);

	const handleOnSetSearchText = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.currentTarget;
		setSearchText(removeCommasAndPercentSign(value));
		callSearch(value);
	};

	const handleOnSearch = value => {
		setSearchText(removeCommasAndPercentSign(value));
		callSearch(value);
	};

	const loadDataFromList = (paginationProp: PaginationSetting = pagination) => {
		getBadgeList(paginationProp, filterByData);
	};

	const changeActionButtonState = (state: boolean) => {
		setDisableActionButton(state);
	};

	const handleSortByOptionChanged = (e: RadioChangeEvent) => {
		const value: BadgeStudioSortOptions = e.target.value;
		setSortField(value);
	};

	const handleSortDirectionOptionChanged = (e: RadioChangeEvent) => {
		const value: SortDirections = e.target.value;
		setSortDirection(value);
	};

	const handleArchiveModalConfirm = () => {
		setShowArchiveModal(false);
		loadDataFromList();
	};

	const handleArchiveModalCancel = () => {
		setShowArchiveModal(false);
	};

	const handleOnChangeView = ({ target: { value } }: RadioChangeEvent) => {
		const tempPagination: PaginationSetting = {
			...pagination,
			PageSize: value === ViewType.List ? 10 : 12,
			PageNumber: 1,
			SortField: getEnumNameFromValue(BadgeStudioSortOptions, BadgeStudioSortOptions.Name),
			SortDirection: SortDirections.Ascend,
		};

		setPagination(tempPagination);
		getBadgeList(tempPagination, filterByData);
		setActiveSort(false);
		setCurrentView(value);
	};

	const handleOnOpenBadge = (badgeData: GraphicDirectoryInfo) => {
		setIsLoading(true);
		handleOnOpen({ Id: badgeData.Id }).finally(() => {
			setIsLoading(false);
		});
	};

	const importMenuItems: MenuProps['items'] = [
		{
			key: '1-import-menu',
			label: (
				<Upload {...uploadProps}>
					<span>{_('ImageFromSystem')}</span>
				</Upload>
			),
		},
		{
			key: '2-import-menu',
			label: _('VelocityBadgeDesigner'),
		},
	];

	const importMenu: MenuProps = {
		items: importMenuItems,
		onClick: e => {
			if (e.key.toString() !== '1-import-menu') {
				dispatch(setActionType(GraphicActionType.ModalDirectory));
			}
		},
	};

	const onCancelBadgeModal = () => {
		dispatch(setActionType(GraphicActionType.Directory));
	};

	const handleOnClickBadge = async (badge: Partial<BadgeDirectoryInfo>): Promise<void> => {
		dispatch(setActionType(GraphicActionType.Directory));
		handleOnOpen(badge);
	};

	return (
		<>
			<Layout className={styles.layout}>
				<Header className={styles.header}>
					<BadgeStudioIcon className={styles.icon} /> <span className={styles.title}>{_('BadgeStudio')}</span>
				</Header>
				<Content className={styles.content}>
					<div className={styles.toolBar}>
						<div className={styles.items}>
							<Search
								allowClear
								className={styles.searchBar}
								maxLength={50}
								placeholder={_('SearchByName')}
								onSearch={handleOnSearch}
								onChange={handleOnSetSearchText}
								value={searchText}
								enterButton
								disabled={disableActionButton}
							/>
							<RadioGroup
								disabled={disableActionButton}
								value={currentView}
								onChange={handleOnChangeView}
								buttonStyle='solid'
								className={styles.switchView}>
								<RadioButton value={ViewType.Card}>
									<AppstoreFilled className={styles.switchViewIcon} />
								</RadioButton>
								<RadioButton value={ViewType.List}>
									<UnorderedListOutlined className={styles.switchViewIcon} />
								</RadioButton>
							</RadioGroup>
							{currentView === ViewType.Card && (
								<Popover
									content={
										<SortByContent
											sortField={sortField}
											sortDirection={sortDirection}
											handleSortByOptionChanged={handleSortByOptionChanged}
											handleSortDirectionOptionChanged={handleSortDirectionOptionChanged}
											onClearSortBy={onClearSortBy}
											onSortBy={onSortBy}
										/>
									}
									placement='bottom'
									open={sortOpen}
									onOpenChange={handleSortOpenChange}>
									<Button
										disabled={disableActionButton}
										icon={<SortAscendingOutlined />}
										className={styles.popOverButton}
										type={activeSort ? 'primary' : 'default'}>
										<span>{_('SortBy')}</span>
										<CaretDownFilled className={styles.popOverDownArrow} />
									</Button>
								</Popover>
							)}
							<Popover
								content={
									!disableActionButton && (
										<FilterByContent
											filterByData={filterByData}
											operators={badgeOperators}
											setFilterByData={setFilterByData}
											onClearFilterBy={onClearFilterBy}
											onFilterBy={onFilterBy}
										/>
									)
								}
								open={open}
								placement='bottom'
								onOpenChange={handleOpenChange}>
								<Button
									disabled={disableActionButton}
									icon={<FilterOutlined />}
									className={styles.popOverButton}
									type={activeFilter ? 'primary' : 'default'}>
									<span>{_('FilterBy')}</span>
									<CaretDownFilled className={styles.popOverDownArrow} />
								</Button>
							</Popover>
						</div>
						<div className={styles.addBadgesContainer}>
							<Dropdown menu={importMenu}>
								<Button>
									<Space>
										<span>{_('ImportBadge')}</span>
										<CaretDownFilled className={styles.popOverDownArrow} />
									</Space>
								</Button>
							</Dropdown>
							<Button
								disabled={disableActionButton || !canEditBadge}
								type='primary'
								title={getPermissionErrorMessage(canEditBadge)}
								onClick={handleOnOpenNewModal}
								icon={<PlusOutlined />}>
								{_('New')}
							</Button>
						</div>
					</div>
					<div>
						<Spin tip={`${_('Loading')}...`} spinning={isLoading} size='large'>
							{currentView === ViewType.Card && (
								<CardView
									badgeListInfo={badgeListInfo}
									pagination={pagination}
									setPagination={setPagination}
									setLoading={setIsLoading}
									loadData={loadDataFromList}
									changeActionButtonState={changeActionButtonState}
									handleOnOpenBadge={handleOnOpenBadge}
								/>
							)}
							{currentView === ViewType.List && (
								<ListView
									badgeListInfo={badgeListInfo}
									pagination={pagination}
									setPagination={setPagination}
									loadData={loadDataFromList}
									changeActionButtonState={changeActionButtonState}
									handleOnOpenBadge={handleOnOpenBadge}
								/>
							)}
						</Spin>
					</div>
					<Button
						disabled={disableActionButton}
						icon={<DeleteOutlined />}
						onClick={() => {
							setShowArchiveModal(true);
						}}>
						{_('Archive')}
					</Button>
					{showArchiveModal && <ArchiveModal onConfirm={handleArchiveModalConfirm} onCancel={handleArchiveModalCancel} />}
				</Content>
			</Layout>
			{hasFlag(graphicActionType, GraphicActionType.ModalDirectory) && (
				<BadgeDirectoryModal onCancel={onCancelBadgeModal} handleOnOpen={handleOnClickBadge} />
			)}
		</>
	);
});

export { BadgeDirectory };
